/**
 * RBAC-3.8 · The role list stays usable as an account grows its own roles (FR-17c, UX-DR2).
 *
 * Pure, no DOM. The list is `components/settings/roles/RolesPermissionsPage.tsx`; its rows come
 * already grouped (own above Joules, by holders then name) from `groupAccountRoles()`.
 *
 * The form the grouping takes, which FR-17c left open:
 *
 * - **Search** filters BOTH groups at once (AC 1), on every word of the query, in any order, against
 *   the name and the description, case and accent insensitive. Each group then reads "n of total",
 *   so a narrowed list never passes for the whole one.
 * - **Two collapsible groups.** The account's roles are always open by default. The Joules roles, a
 *   fixed reference set the admin cannot edit, fold away once the account has
 *   `JOULES_COLLAPSE_AT` roles of its own, because that is the point at which they push the
 *   account's roles off the first screen. A fold never hides the selected role, and never hides a
 *   search match: while searching every group is open.
 * - The nav scrolls on its own inside the pane rather than lengthening the page (component).
 */

import type { AccountRole, GroupedRoles } from './accountRoles';

/** Own-role count at which the Joules group starts folded. */
export const JOULES_COLLAPSE_AT = 8;

export type RoleGroupKey = 'own' | 'joules';

/** Lowercased, accent folded, whitespace collapsed. Blank is ''. */
export function normaliseRoleQuery(q: string): string {
  return q
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export interface FilteredRoleGroups extends GroupedRoles {
  searching: boolean;
  ownTotal: number;
  joulesTotal: number;
}

const matches = (role: AccountRole, words: readonly string[]) => {
  const hay = normaliseRoleQuery(`${role.name} ${role.description ?? ''}`);
  return words.every((w) => hay.includes(w));
};

/** AC 1: one query across the account's roles and the Joules ones. Order is kept. */
export function filterRoleGroups(groups: GroupedRoles, query: string): FilteredRoleGroups {
  const q = normaliseRoleQuery(query);
  const totals = { ownTotal: groups.own.length, joulesTotal: groups.joules.length };
  if (!q) return { own: groups.own, joules: groups.joules, searching: false, ...totals };
  const words = q.split(' ');
  return {
    own: groups.own.filter((r) => matches(r, words)),
    joules: groups.joules.filter((r) => matches(r, words)),
    searching: true,
    ...totals,
  };
}

/** The count beside a group caption: "20", or "3 of 20" while searching. */
export function groupCountLabel(shown: number, total: number, searching: boolean): string {
  return searching ? `${shown} of ${total}` : String(total);
}

/** AC 2: whether a group renders open. */
export function groupOpen(input: {
  group: RoleGroupKey;
  /** How many roles the account has of its own, unfiltered. */
  ownCount: number;
  selectedInGroup: boolean;
  searching: boolean;
  /** The admin's own toggle this visit, if any. */
  toggled: boolean | undefined;
}): boolean {
  if (input.searching) return true;
  // An explicit toggle wins over the selected role, or the chevron on that group would do nothing.
  if (input.toggled !== undefined) return input.toggled;
  if (input.selectedInGroup) return true;
  return input.group === 'own' || input.ownCount < JOULES_COLLAPSE_AT;
}

export function noMatchMessage(query: string): string {
  return `No roles match “${query.trim()}”.`;
}
